import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { Text, HStack, VStack } from "@chakra-ui/react";
import { SearchFilter, SearchFilterType } from "gql/graphql";
import UserTagGroupSelector, {
  Option,
} from "modules/selectors/user-tag-group-selector";
import { TagIcon } from "modules/icons";
import { useTags } from "modules/providers/tags-provider";

interface IProps {
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
}

const FiltersDrawerTagSelector = ({
  newSearchFilters,
  setNewSearchFilters,
}: IProps) => {
  const { tagsById } = useTags();

  const getOptionValues = useCallback(
    (type: SearchFilterType): Option[] => {
      const filter = newSearchFilters.find((filter) => filter.type === type);
      if (!filter || !filter.objectIdListValue) return [];
      if (type === SearchFilterType.TagIdList) {
        return filter.objectIdListValue.map((id) => ({
          id,
          type: "tag",
          active: tagsById[id]?.active ?? false,
        }));
      }

      return [];
    },
    [newSearchFilters, tagsById],
  );

  const tagValues = useMemo<Option[]>(
    () => [...getOptionValues(SearchFilterType.TagIdList)],
    [getOptionValues],
  );

  const onTagsSelectorChanged = useCallback(
    (options: Option[]) => {
      setNewSearchFilters((prev) => {
        const _prev = [...prev].filter(
          (filter) => filter.type !== SearchFilterType.TagIdList,
        );
        if (options.length > 0) {
          _prev.push({
            type: SearchFilterType.TagIdList,
            objectIdListValue: options.map((tag) => tag.id),
          });
        }
        return _prev;
      });
    },
    [setNewSearchFilters],
  );

  return (
    <VStack w="full">
      <HStack w="full">
        <TagIcon fill={colors.blackSecondary} boxSize={5} />
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          Tags
        </Text>
      </HStack>
      <UserTagGroupSelector
        types={["tag"]}
        isMultiSelect
        onSelectedOptionsChanged={onTagsSelectorChanged}
        allowInactive
        value={tagValues}
        placeHolder="Enter tags"
        menuPlacement="top"
      />
    </VStack>
  );
};

export default memo(FiltersDrawerTagSelector);
